<template>
    <div class="bg-white">
        <div
            v-if="searchLink && isSearch"
            class="flex-1 px-6 py-3 flex flex-col md:flex-row justify-between bg-gray-50 space-y-4 md:space-y-0"
        >
            <div class="flex-1 flex items-center">
                <div class="w-full flex md:ml-0">
                    <label for="search-field" class="sr-only"> Search </label>
                    <div class="relative w-full flex text-primary-light">
                        <div
                            class="absolute inset-y-0 left-0 flex items-center pointer-events-none"
                        >
                            <SearchIcon/>
                        </div>
                        <input
                            id="search-field"
                            v-model="searchTable"
                            class="flex-1 border-0 block w-full h-full pl-10 pr-3 py-2 bg-transparent border-transparent text-primary placeholder-primary-light focus:placeholder-primary-disabled focus:outline-none focus:ring-0 focus:border-transparent font-medium text-sm"
                            placeholder="Search"
                            type="search"
                            name="search"
                            autocomplete="off"
                            @input="getSearchResults"
                        />
                    </div>
                </div>
            </div>
            <div class="flex items-center md:space-x-6 md:ml-6">
                <Button :isDark="true" v-if="action" :href="action.url">
                    <span class="ml-2">{{ action.text }}</span>
                </Button>
            </div>
        </div>
        <table class="min-w-full">
            <thead class="bg-gray-50 border-b">
            <tr>
                <th
                    scope="col"
                    class="px-6 py-2 text-left text-xs font-medium text-primary-light capitalize tracking-wider"
                    v-for="(field, index) in fields"
                    :key="index"
                >
                    <slot :name="`th${field.key}`">
                        <div
                            class="flex"
                            :class="{
                                'cursor-pointer': field.sortable,
                            }"
                            @click="sort(field, sortByASC ? 'asc' : 'desc')"
                        >
                            {{ field.label }}
                            <div
                                class="flex flex-col ml-1"
                                v-if="field.sortable"
                            >
                                <SortASCIcon
                                    class="w-2 h-2"
                                    :active="
                                        sortKey === field.sortKey && !sortByASC
                                    "
                                />
                                <SortDSCIcon
                                    class="w-2 h-2 -mt-[2px]"
                                    :active="
                                        sortKey === field.sortKey && sortByASC
                                    "
                                />
                            </div>
                        </div>
                    </slot>
                </th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="(it, ind) in isPaginate ? tableData.data : tableData"
                :key="ind"
                :style="{ borderSpacing: '4px' }"
                :class="`${
                        isTrClass
                            ? it[trClass.filedName]
                                ? trClass.activeClass
                                : trClass.notActiveClass
                            : ''
                    }`"
            >
                <td
                    class="p-6 text-sm font-medium text-primary-light"
                    v-for="(field, index) in fields"
                    :key="index"
                >
                    <slot :name="field.key" :item="it"
                    >{{ it[field.key] }}
                    </slot>
                </td>
            </tr>
            </tbody>
        </table>
        <div

            class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between px-5 py-2"
        >
            <div>
                <p class="text-sm font-medium text-primary-light" v-if="isPaginate">
                    Showing
                    <span class="font-medium">{{ tableData.from }}</span>
                    to
                    <span class="font-medium">{{ tableData.to }}</span>
                    of
                    <span class="font-medium">{{ tableData.total }}</span>
                    results
                </p>
                <p v-else>
                    Found {{ items.length }} Results
                </p>
            </div>
            <div>
                <Pagination
                    :data="tableData"
                    @pagination-change-page="getResults"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import Pagination from "laravel-vue-pagination";
import SpinnerIcon from "@/Components/Icons/Spinner.vue";
import SortASCIcon from "@/Components/Icons/SortASC.vue";
import SortDSCIcon from "@/Components/Icons/SortDSC.vue";
import SearchIcon from "@/Components/Icons/Search.vue";
import Button from "@/Components/Button.vue";
import {ref, watch} from "vue";
import {Inertia} from "@inertiajs/inertia";

const props = defineProps({
    action: Object,
    fields: {
        type: Object,
        default: [],
    },
    items: {
        type: Object,
        default: [],
    },
    isPaginate: {
        type: Boolean,
        default: false,
    },
    paginationLink: {
        type: String,
        default: "",
    },
    searchLink: {
        type: String,
        default: "",
    },
    isTrClass: {
        type: Boolean,
        default: false,
    },
    isSearch: {
        type: Boolean,
        default: false,
    },
    trClass: {
        filedName: {
            type: String,
            default: "",
        },
        activeClass: {
            type: String,
            default: "",
        },
        notActiveClass: {
            type: String,
            default: "",
        },
    },
});
const tableData = ref(props.items);
const sortKey = ref("");
const sortByASC = ref(true);
const searchTable = ref("");
const loading = ref(false);

watch(
    () => props.items,
    (n, o) => {
        tableData.value = n;
    }
);

function sort(field, direction) {
    if (!field.sortable) {
        return false;
    }
    sortKey.value = field.sortKey;
    sortByASC.value = !sortByASC.value;
    console.log(props.searchLink);
    window.axios
        .get(props.searchLink, {
            params: {
                sortBy: field.sortKey,
                direction,
                keyword: searchTable.value,
            },
        })
        .then((res) => {
            tableData.value = res.data;
            loading.value = false;
        });
}

const getSearchResults = _.debounce(function () {
    loading.value = true;
    window.axios
        .get(props.searchLink, {
            params: {keyword: searchTable.value},
        })
        .then((res) => {
            tableData.value = res.data;
            loading.value = false;
        });
}, 500);

function getResults(page = 1) {
    Inertia.get(`${props.paginationLink}?page=${page}`);
}
</script>


<style>
.pagination {
    display: inline-flex;
}

.pagination .active .page-link {
    background: #0c1824 !important;
    color: white;
}

.page-link:hover {
    background: #0c1824;
    color: white;
}

.page-link {
    background: white;
    padding: 8px 16px;
    display: flex;
    align-content: center;
    justify-content: center;
    cursor: pointer;
    transition-duration: 300ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-property: all;
}

.page-item {
    border: 1px solid #d1d5db;
    overflow: hidden;
}

.page-item:first-child {
    border-radius: 5px 0px 0px 5px;
}

.page-item:last-child {
    border-radius: 0px 5px 5px 0px;
}
</style>
